import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { SessionUser, Role } from "@/types/session";
import { useUpdateUser } from "./queries";
import { HttpError } from "@shared/http/client";

const ROLES: Role[] = ["Administrador", "Evaluador", "Programador", "Auditoria"];

const schema = z.object({
  name: z.string().min(1, "Nombre requerido").max(160),
  email: z.string().email("Correo inválido").max(160),
  role: z.enum(ROLES as [Role, ...Role[]]),
  status: z.enum(["Activo", "Inactivo"]),
  password: z.string().max(200).optional().or(z.literal("")),
});

type FormValues = z.infer<typeof schema>;

type Props = {
  user: SessionUser | null;
  onClose: () => void;
};

export function UserEditDialog({ user, onClose }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const updateMut = useUpdateUser();
  const open = user !== null;

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", role: "Evaluador", status: "Activo", password: "" },
  });

  useEffect(() => {
    if (user) reset({ name: user.name, email: user.email, role: user.role, status: user.status, password: "" });
  }, [user, reset]);

  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    if (!open && dlg.open) dlg.close();
    if (!open) setServerError(null);
  }, [open]);

  const onSubmit = async (values: FormValues) => {
    if (!user) return;
    try {
      await updateMut.mutateAsync({
        id: user.id,
        payload: {
          name: values.name,
          email: values.email,
          role: values.role,
          status: values.status,
          password: values.password || undefined,
        },
      });
      onClose();
    } catch (e) {
      setServerError(e instanceof HttpError ? e.message : "Error actualizando usuario.");
    }
  };

  return (
    <dialog ref={dialogRef} onCancel={onClose} onClose={onClose} className="detail-dialog">
      <div className="detail-dialog-head">
        <div>
          <h3 className="detail-dialog-title">Editar usuario</h3>
          <div className="detail-dialog-sub">{user?.username}</div>
        </div>
        <button type="button" className="detail-dialog-close" onClick={onClose}>✕</button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="detail-dialog-body">
        <div className="wizard-grid">
          <label className="form-field form-field--full">
            <span>Nombre</span>
            <input {...register("name")} className={errors.name ? "campo-requerido-error" : ""} />
            {errors.name && <div className="alert-inline alert-inline--danger">{errors.name.message}</div>}
          </label>

          <label className="form-field form-field--full">
            <span>Correo electrónico</span>
            <input type="email" {...register("email")} className={errors.email ? "campo-requerido-error" : ""} />
            {errors.email && <div className="alert-inline alert-inline--danger">{errors.email.message}</div>}
          </label>

          <label className="form-field">
            <span>Rol</span>
            <select {...register("role")}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>

          <label className="form-field">
            <span>Estado</span>
            <select {...register("status")}>
              <option value="Activo">Activo</option>
              <option value="Inactivo">Inactivo</option>
            </select>
          </label>

          <label className="form-field form-field--full">
            <span>Nueva contraseña (opcional)</span>
            <input type="password" {...register("password")} placeholder="Dejar vacío para no cambiar" />
          </label>
        </div>

        {serverError && (
          <div className="alert-inline alert-inline--danger alert-inline--md mt-8">⚠️ {serverError}</div>
        )}

        <div className="detail-dialog-actions" style={{ borderTop: 0, paddingTop: 16 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn btn-primary" disabled={updateMut.isPending}>
            {updateMut.isPending ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </form>
    </dialog>
  );
}
